import React, { useRef, useEffect, useState } from 'react';
import { MapPin, Star } from 'lucide-react';
import { getUserLocations, addUserLocation } from '../services/api';

export default function LocationAutocomplete({ 
  value, 
  onChange, 
  onPlaceSelect,
  placeholder = "Enter location",
  icon = "pickup" // "pickup" or "dropoff"
}) {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const justSelectedRef = useRef(false);

  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [savedLocations, setSavedLocations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google?.maps?.places) {
        setIsGoogleLoaded(true);
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        const dummyDiv = document.createElement('div');
        placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        return true;
      }
      return false;
    };

    if (checkGoogleMaps()) return;

    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.google?.maps?.places) {
        console.error('Google Maps failed to load after 10 seconds');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Fetch saved locations
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const res = await getUserLocations();
        if (res.success && res.locations) {
          setSavedLocations(res.locations);
        }
      } catch (err) {
        console.error('Failed to load saved locations:', err);
      }
    };
    fetchSaved();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter and load suggestions
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (!isFocused) {
      setIsOpen(false);
      return;
    }

    if (!value || value.trim().length < 2) {
      if (savedLocations.length > 0) {
        setSuggestions(savedLocations.map(loc => ({
          type: 'saved',
          id: loc.id,
          name: loc.location_name,
          address: loc.address,
          lat: parseFloat(loc.lat),
          lng: parseFloat(loc.lng)
        })));
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
      setIsLoading(false);
      return;
    }

    const query = value.toLowerCase().trim();
    // Filter local saved locations first
    const localMatches = savedLocations.filter(loc => 
      loc.location_name?.toLowerCase().includes(query) || 
      loc.address?.toLowerCase().includes(query)
    );

    if (localMatches.length > 0) {
      setSuggestions(localMatches.map(loc => ({
        type: 'saved',
        id: loc.id,
        name: loc.location_name,
        address: loc.address,
        lat: parseFloat(loc.lat),
        lng: parseFloat(loc.lng)
      })));
      setIsOpen(true);
      setIsLoading(false);
    } else {
      // Only if no saved match -> call Google Maps Places API
      if (!isGoogleLoaded || !autocompleteServiceRef.current) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      const delayDebounceFn = setTimeout(() => {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country: 'pk' }
          },
          (predictions, status) => {
            setIsLoading(false);
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions.map(pred => ({
                type: 'google',
                id: pred.place_id,
                name: pred.structured_formatting?.main_text || pred.description,
                address: pred.description,
                placeId: pred.place_id
              })));
              setIsOpen(true);
            } else {
              setSuggestions([]);
              setIsOpen(false);
            }
          }
        );
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [value, savedLocations, isGoogleLoaded, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (!value || value.trim().length < 2) {
      if (savedLocations.length > 0) {
        setSuggestions(savedLocations.map(loc => ({
          type: 'saved',
          id: loc.id,
          name: loc.location_name,
          address: loc.address,
          lat: parseFloat(loc.lat),
          lng: parseFloat(loc.lng)
        })));
        setIsOpen(true);
      }
    } else {
      setIsOpen(true);
    }
  };

  const handleSelect = async (suggestion) => {
    justSelectedRef.current = true;
    setIsOpen(false);
    setIsFocused(false);

    if (suggestion.type === 'saved') {
      const locationData = {
        address: suggestion.address,
        lat: suggestion.lat,
        lng: suggestion.lng
      };
      onChange(suggestion.address);
      onPlaceSelect(locationData);
    } else if (suggestion.type === 'google') {
      if (!placesServiceRef.current) return;
      
      setIsLoading(true);
      placesServiceRef.current.getDetails(
        {
          placeId: suggestion.placeId,
          fields: ['geometry', 'formatted_address', 'name']
        },
        async (place, status) => {
          setIsLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
            const fullAddress = place.formatted_address || place.name;
            const locationData = {
              address: fullAddress,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            
            onChange(fullAddress);
            onPlaceSelect(locationData);

            // Save to user locations
            try {
              const alreadySaved = savedLocations.some(
                loc => Math.abs(parseFloat(loc.lat) - locationData.lat) < 0.0001 &&
                       Math.abs(parseFloat(loc.lng) - locationData.lng) < 0.0001
              );
              
              if (!alreadySaved) {
                const saveResponse = await addUserLocation({
                  location_name: place.name || 'Recent Location',
                  address: fullAddress,
                  lat: locationData.lat,
                  lng: locationData.lng,
                  is_primary: false
                });
                
                if (saveResponse.success && saveResponse.location) {
                  setSavedLocations(prev => [saveResponse.location, ...prev]);
                }
              }
            } catch (err) {
              console.error('Failed to auto-save location:', err);
            }
          }
        }
      );
    }
  };

  // If Google Maps not loaded yet, show disabled state
  if (!isGoogleLoaded) {
    return (
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
        <input
          type="text"
          placeholder="Loading maps..."
          disabled
          className="w-full pl-10 pr-4 py-3 border rounded-lg bg-gray-100 text-gray-500"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
          icon === 'pickup' ? 'text-emerald-500' : 'text-red-500'
        }`} />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          className="w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
          {suggestions.map((suggestion) => (
            <li
              key={`${suggestion.type}-${suggestion.id}`}
              onClick={() => handleSelect(suggestion)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-start space-x-3 border-b border-gray-50 last:border-0 transition-colors duration-150"
            >
              {suggestion.type === 'saved' ? (
                <div className="p-1 bg-amber-50 rounded-full text-amber-500 mt-0.5">
                  <Star className="w-4 h-4 fill-amber-500" />
                </div>
              ) : (
                <div className="p-1 bg-gray-50 rounded-full text-gray-400 mt-0.5">
                  <MapPin className="w-4 h-4" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {suggestion.name}
                  </p>
                  {suggestion.type === 'saved' && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      Saved
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5 text-left">
                  {suggestion.address}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}